import React from 'react';
import {
  TextInput,
  ReferenceInput,
  SelectInput,
  required,
  Create,
  Edit,
  FormDataConsumer
} from 'react-admin';
import { FormSpy } from 'react-final-form';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core';
import AutocompleteInput from '../../../core/components/forked/input/AutocompleteInput';
import { fieldLengthValidator } from '../../../core/utils/index';
import SimpleFormWithSave from '../../../core/components/form/SimpleFormWithSave';
import { isCountryWithState } from '../../investors/utils';

const useStyles = makeStyles({
  fullWidth: {
    width: '100%'
  },
  autocompleteSuggestions: {
    zIndex: 3010,
    backgroundColor: '#ffffff'
  }
});

const ShowStates = props => {
  const classes = useStyles();
  return (
    <AutocompleteInput
      optionText="text"
      autoComplete="off"
      variant="standard"
      clientSideFilterOnly={true}
      disabled={props.choices.length === 0}
      options={{
        suggestionsContainerProps: {
          className: classes.autocompleteSuggestions
        }
      }}
      id="autocomplete-input-state"
      menuId="autocomplete-input-menu-state"
      getItemId={itemIndex => `autocomplete-input-menu-item-state-${itemIndex}`}
      {...props}
    />
  );
};

const UserAddressFormFields = ({
  operation,
  useraddresstypes,
  classes,
  form
}) => (
  <Grid container spacing={2}>
    <Grid item xs={12} sm={12} md={12} lg={12}>
      <SelectInput
        disabled={operation !== 'Create'}
        label="Address Type"
        source="addressTypeId"
        optionText="name"
        validate={[required()]}
        choices={useraddresstypes}
        variant="standard"
      />
    </Grid>
    <Grid item xs={12} sm={12} md={12} lg={12}>
      <TextInput
        label="Address Line 1"
        source="addressLine1"
        validate={[required(), fieldLengthValidator(254)]}
        options={{ autoComplete: 'off' }}
        variant="standard"
      />
    </Grid>
    <Grid item xs={12} sm={12} md={12} lg={12}>
      <TextInput
        label="Address Line 2"
        source="addressLine2"
        validate={[fieldLengthValidator(254)]}
        options={{ autoComplete: 'off' }}
        variant="standard"
      />
    </Grid>
    <Grid item xs={12} sm={12} md={12} lg={12}>
      <TextInput
        label="Address Line 3"
        source="addressLine3"
        validate={[fieldLengthValidator(254)]}
        options={{ autoComplete: 'off' }}
        variant="standard"
      />
    </Grid>
    <Grid item xs={12} sm={12} md={12} lg={12}>
      <TextInput
        source="city"
        validate={[required(), fieldLengthValidator(128)]}
        options={{ autoComplete: 'off' }}
        variant="standard"
      />
    </Grid>
    <Grid item xs={12} sm={12} md={12} lg={12}>
      <TextInput
        label="ZIP/Postal Code"
        source="postalCode"
        validate={[fieldLengthValidator(128)]}
        options={{ autoComplete: 'off' }}
        variant="standard"
      />
    </Grid>
    <Grid item xs={12} sm={12} md={12} lg={12}>
      <ReferenceInput
        label="Country"
        source="country"
        resource="countries"
        reference="countries"
        filterToQuery={searchText => ({ text: searchText + '*' })}
        onChange={() => {
          form.change('state', undefined);
        }}
        perPage={-1}
        validate={[required()]}
      >
        <AutocompleteInput
          optionText="text"
          autoComplete="off"
          variant="standard"
          clientSideFilterOnly={true}
          options={{
            suggestionsContainerProps: {
              className: classes.autocompleteSuggestions
            }
          }}
          id="autocomplete-input-country"
          menuId="autocomplete-input-menu-country"
          getItemId={itemIndex =>
            `autocomplete-input-menu-item-country-${itemIndex}`
          }
        />
      </ReferenceInput>
    </Grid>
    <Grid item xs={12} sm={12} md={12} lg={12}>
      <FormDataConsumer>
        {({ formData }) =>
          formData &&
          formData.country &&
          isCountryWithState(formData.country) && (
            <ReferenceInput
              label="State/Province"
              source="state"
              resource="states"
              reference="states"
              perPage={-1}
              filter={{ countryCode: formData.country || '' }}
              filterToQuery={searchText => ({ text: searchText + '*' })}
              parse={value => {
                return value ? value.split(':')[0] : null;
              }}
              format={value => {
                return value + ':' + formData.country;
              }}
            >
              <ShowStates />
            </ReferenceInput>
          )
        }
      </FormDataConsumer>
    </Grid>
  </Grid>
);

const getAvailableAddressTypes = (
  useraddresstypes,
  useraddresses,
  currentUserAddress
) => {
  const existingAddressTypes = useraddresses.list.ids
    .filter(id => id !== currentUserAddress)
    .map(id => useraddresses.data[id].addressTypeId);
  const availableIds = useraddresstypes.list.ids.filter(
    id => !existingAddressTypes.includes(id)
  );
  return availableIds.map(id => useraddresstypes.data[id]);
};

const UserAddressForm = props => {
  const {
    handleSubmit,
    formRef,
    operation,
    showDialog,
    handleDialogToggle,
    crudGetManyReference,
    fetchEnd,
    fetchStart,
    crudGetList,
    loaded,
    currentSort,
    setSort,
    useraddresses,
    useraddresstypes,
    popupTitle,
    okButtonLabel,
    ...operationProps
  } = props;

  const classes = useStyles();

  if (operation === 'Create') {
    return (
      <Create {...operationProps} className={classes.fullWidth}>
        <SimpleFormWithSave
          toolbar={null}
          // We override the redux-form name to avoid collision with the react-admin main form
          resource="useraddresses"
          // We override the redux-form save prop to handle the submission ourselves
          onSave={handleSubmit}
          variant="standard"
          margin="normal"
        >
          <FormSpy>
            {props => {
              const form = props.form;
              formRef.current = form;
              return (
                <UserAddressFormFields
                  operation={operation}
                  useraddresstypes={getAvailableAddressTypes(
                    useraddresstypes,
                    useraddresses
                  )}
                  classes={classes}
                  form={form}
                />
              );
            }}
          </FormSpy>
        </SimpleFormWithSave>
      </Create>
    );
  } else {
    return (
      <Edit {...operationProps} className={classes.fullWidth}>
        <SimpleFormWithSave
          toolbar={null}
          // We override the redux-form name to avoid collision with the react-admin main form
          resource="useraddresses"
          // We override the redux-form save prop to handle the submission ourselves
          onSave={handleSubmit}
          variant="standard"
          margin="normal"
        >
          <FormSpy>
            {props => {
              const form = props.form;
              formRef.current = form;
              return (
                <UserAddressFormFields
                  operation={operation}
                  useraddresstypes={getAvailableAddressTypes(
                    useraddresstypes,
                    useraddresses,
                    operationProps.id
                  )}
                  classes={classes}
                  form={form}
                />
              );
            }}
          </FormSpy>
        </SimpleFormWithSave>
      </Edit>
    );
  }
};

export default UserAddressForm;
